import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';

// shorter on phones so each chart doesn't take a whole screen
const chartStyle = { height: typeof window !== 'undefined' && window.innerWidth < 640 ? 260 : 350, width: '100%' };

type BarChartProps = {
    title: string;
    data: number[];
    xAxis: string[];
};

const BarChart: React.FC<BarChartProps> = ({ title, data, xAxis }) => {
    const { t } = useTranslation();
    const formattedData = data.map((value: number) => {
        const isNegative = value < 0;
        return {
            value,
            itemStyle: {
                color: isNegative ? '#ef4444' : '#10b981',
                borderColor: isNegative ? '#dc2626' : '#059669',
                borderWidth: 2,
                borderRadius: [4, 4, 0, 0],
                shadowColor: isNegative ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)',
                shadowBlur: 8,
                shadowOffsetY: 4,
            },
        };
    });

    const option = {
        backgroundColor: 'transparent',
        title: {
            text: title,
            left: 'center',
            top: 20,
            textStyle: {
                fontSize: 18,
                fontWeight: 'bold',
                color: '#1f2937',
                fontFamily: "Roboto, sans-serif",
            },
        },
        grid: {
            top: 60,
            bottom: 40,
            left: 50,
            right: 30,
            containLabel: true,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
                shadowStyle: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    shadowBlur: 10,
                }
            },
            backgroundColor: '#ffffff',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            borderRadius: 8,
            padding: [12, 16],
            textStyle: {
                color: '#374151',
                fontSize: 13,
                fontWeight: '500',
            },
            formatter: (params: any) => {
                const item = params[0];
                const month = item.name;
                const result = item.value;
                const isPositive = result >= 0;
                const resultFormatted = `${isPositive ? '+' : '-'}$${Math.abs(result).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                const resultColor = isPositive ? '#059669' : '#dc2626';
                const icon = isPositive ? '📈' : '📉';

                const monthLabel = String(month).split(' ')[0];
                const yearLabel = String(month).split(' ')[1];
                const displayMonth = t(monthLabel);
                const itemColor = item.color ?? item.itemStyle?.color ?? (isPositive ? '#059669' : '#dc2626');

                return `
                  <div style="padding: 4px 0;">
                    <div style="font-size: 14px; font-weight: 600; color: #1f2937; margin-bottom: 8px;">
                      ${icon} ${displayMonth} ${yearLabel}
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <div style="width: 12px; height: 12px; border-radius: 50%; background: ${itemColor};"></div>
                      <span style="color: #6b7280; margin-right: 6px;">${t('Profit')}:</span>
                      <span style="color: ${resultColor}; font-weight: 600; font-size: 14px;">${resultFormatted}</span>
                    </div>
                  </div>
                `;
            },
        },
        xAxis: {
            type: 'category',
            data: xAxis,
            axisLabel: {
                interval: 0,
                rotate: 0,
                color: '#6b7280',
                fontSize: 12,
                fontWeight: '500',
                formatter: (value: string) => {
                    const monthLabel = String(value).split(' ')[0];
                    const yearLabel = String(value).split(' ')[1];
                    const displayMonth = t(monthLabel);
                    return `${displayMonth} ${yearLabel}`;
                },
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#e5e7eb',
                    width: 1,
                }
            },
            axisTick: {
                show: true,
                lineStyle: {
                    color: '#e5e7eb',
                }
            },
            splitLine: {
                show: false,
            }
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                color: '#6b7280',
                fontSize: 12,
                formatter: (value: number) => `$${value.toLocaleString()}`,
            },
            axisLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            splitLine: {
                lineStyle: {
                    color: '#f3f4f6',
                    type: 'dashed',
                }
            }
        },
        series: [
            {
                type: 'bar',
                data: formattedData,
                barWidth: '60%',
                barCategoryGap: '20%',
                barGap: '10%',
                itemStyle: {
                    color: '#10b981',
                    borderColor: '#059669',
                    borderWidth: 2,
                    borderRadius: [4, 4, 0, 0],
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 15,
                        shadowColor: 'rgba(16, 185, 129, 0.4)',
                        borderColor: '#047857',
                        borderWidth: 3,
                    },
                },
                animationDelay: (idx: number) => idx * 100,
                animationDuration: 800,
            },
        ],
        animation: true,
        animationDuration: 1000,
        animationEasing: 'cubicOut',
    };

    return <ReactECharts option={option} style={chartStyle} />;
};


interface DashboardChartsProp {
    monthlyAnalysis: MonthlyAnalysis[]
    pdfShow?: boolean
}

interface BaseAnalysis {
    trades: number
    profit: number
    win: number
    losses: number
    win_rate: number
}

interface MonthlyAnalysis extends BaseAnalysis {
    month: string
    month_name: string
}


const DashboardCharts = (props: DashboardChartsProp) => {
    const { t } = useTranslation();
    const { monthlyAnalysis, pdfShow } = props

    return (
        <div className={`space-y-6 ${pdfShow ? 'max-w-full' : 'min-w-full'}`}>
            <div className={`portlet ${pdfShow ? 'max-w-full' : 'min-w-full'}`} style={{
                width: pdfShow ? "1075px" : '100%',
            }}>
                <BarChart
                    title={t('ProfitbyMonth')}
                    xAxis={monthlyAnalysis.map(item => item.month_name)}
                    data={monthlyAnalysis.map(item => item.profit)}
                />
            </div>
        </div>
    );
};

export default DashboardCharts;
